import {
  getUniqueSubQrcode,
  IGetUniqueSubQrcodeParams,
  PositionCode,
} from '@rendora/common/entities/analysis_backend';
import { getChannel } from '@rendora/common/utils/report';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useContactStore } from '../store';

export const getDynamicQrcode = async (code: PositionCode) => {
  const md5 = (await import('js-md5')).default.md5;
  const channel = getChannel();
  const params: IGetUniqueSubQrcodeParams = {
    gclid: channel.gclid,
    utm_source: channel.utm_source,
    live_code: code,
    source: channel.source,
    source_from: 'youyan',
  };
  const signStr = Object.keys(params)
    .sort((a, b) => a.localeCompare(b))
    .filter(key => params[key as keyof IGetUniqueSubQrcodeParams])
    .map(key => `${key}=${params[key as keyof IGetUniqueSubQrcodeParams]}`)
    .join(';');
  const sign = md5(signStr);
  const res = await getUniqueSubQrcode({ ...params, sign });

  if (res.data) {
    return res.data;
  }
  return null;
};

export const useQrCode = (code: PositionCode) => {
  const qrCode = ref<string>('');
  const scanStatus = ref<'waiting' | 'scanned' | 'expired' | 'error'>('waiting');
  const scanResult = ref<any>(null);
  const error = ref<string | null>(null);
  const loading = ref(true);
  const qrStore = useContactStore();

  // 强制刷新计数器，确保响应性
  const forceUpdate = ref(0);
  let refreshTimer: NodeJS.Timeout | null = null;

  const finalQrCode = computed(() => {
    // 使用forceUpdate来强制重新计算
    forceUpdate.value;

    const storeData = qrStore.getQrCodeData(code);
    const staticQr = qrCode.value;

    return storeData?.link || staticQr;
  });

  // 监听store变化，确保响应性
  watch(
    () => qrStore.getQrCodeData(code),
    newData => {
      if (newData?.link) {
        // 强制触发computed重新计算
        forceUpdate.value++;
      }
    },
    { deep: true },
  );

  onMounted(async () => {
    try {
      loading.value = true;

      // 首先获取静态二维码作为初始值
      const staticQr = await getDynamicQrcode(code);
      qrCode.value = staticQr || '';

      loading.value = false;

      // 定期检查store变化，确保响应性（应急方案）
      refreshTimer = setInterval(() => {
        const currentStoreData = qrStore.getQrCodeData(code);
        if (currentStoreData?.link && currentStoreData.link !== qrCode.value) {
          forceUpdate.value++;
        }
      }, 2000); // 每2秒检查一次
    } catch (err: any) {
      console.error('获取静态二维码失败:', err.message || err);
      error.value = err.message || '获取二维码失败';
      loading.value = false;
    }
  });

  onUnmounted(() => {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
  });

  return {
    qrCode: finalQrCode,
    scanStatus,
    scanResult,
    qr: qrCode.value,
    error,
    loading,
    finalQrCode: qrStore.getQrCodeData(code)?.link,
  };
};
